import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { PageContainer } from '../../../../../components/page-container/page-container'
import { styles, texts } from './collection-order-detail.styles'
import { paths } from '../../../../../router/paths'
import {
  formatCurrencyGTQ,
  formatDateShort,
  mapCollectionOrderStatus,
} from '../../../../../utils/formatters'
import { formatPhoneGT } from '../../../../../utils/masks/formatPhoneGT'
import { useEffect, useState } from 'react'
import { PrintReceiptModal } from '../../../../../components/print-receipt-modal/print-receipt-modal'

interface CollectionOrderDetailData {
  CollectionOrderId: number
  Status: number
  BankName: string
  PrincipalValue: number
  Token: string
  PayerPhone?: string
  CreationDate?: string
}

export const CollectionOrderDetail = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const location = useLocation() as any
  const [data, setData] = useState<CollectionOrderDetailData | null>(null)
  const [printOpen, setPrintOpen] = useState(false)

  // Tentamos obter dados do state (enviados pela lista). Caso não encontre, poderíamos (futuro) buscar store.
  useEffect(() => {
    if (location.state?.collectionOrder) {
      setData(location.state.collectionOrder)
    } else if (id) {
      // Fallback: procurar no Redux store sem nova requisição (opcional se desejado)
      try {
        const globalState: any = (window as any).__APP_STORE__?.getState?.() // placeholder se houver injeção futura
        const found = globalState?.collectionOrder?.collectionOrders?.find(
          (o: any) => o.CollectionOrderId === Number(id),
        )
        if (found) setData(found)
      } catch {
        // ignorar
      }
    }
  }, [location.state, id])

  const getStatusText = (status: number) => mapCollectionOrderStatus(status)

  const getStatusStyle = (status: number): string => {
    if (status === 1) return styles.statusAwaitingPayment
    if (status === 2) return styles.statusSettled
    if (status === 3) return styles.statusCanceledRefund
    if (status === 4) return styles.statusExpired
    // Qualquer outro (códigos inválidos) => erro
    return styles.statusError
  }

  const handlePrint = () => setPrintOpen(true)

  const handleGoBack = () => {
    sessionStorage.setItem('dailySalesPreserve', '1')
    navigate(paths.dailySales)
  }

  const formatTimeWithSeconds = (dateString?: string) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      return date.toLocaleTimeString('es-GT', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch {
      return dateString || ''
    }
  }

  if (!data) {
    return (
      <PageContainer type="authenticated" title={texts.title}>
        <div className={styles.container}>
          <div className="text-center text-gray-500">
            Registro no encontrado.
          </div>
          <div className={styles.actionsContainer}>
            <button className={styles.backButton} onClick={handleGoBack}>
              {texts.goBack}
            </button>
          </div>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer type="authenticated" title={texts.title}>
      <div className={styles.container}>
        <div className={styles.detailContainer}>
          <div className={styles.headerCard}>
            <div className={styles.headerTitle}>
              <h2 className={styles.title}>{texts.title}</h2>
              <span
                className={`${styles.statusBadge} ${getStatusStyle(data.Status)}`}
              >
                {getStatusText(data.Status)}
              </span>
            </div>
            <div className={styles.headerSubtitle}>
              {texts.saleId}: {data.CollectionOrderId}
            </div>
          </div>

          <div className={styles.infoCard}>
            <h3 className={styles.cardTitle}>Información</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{texts.token}</span>
                <span className={styles.infoValue}>{data.Token}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{texts.amount}</span>
                <span className={styles.infoValueAmount}>
                  {formatCurrencyGTQ(data.PrincipalValue)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{texts.payerPhone}</span>
                <span className={styles.infoValue}>
                  {data.PayerPhone ? formatPhoneGT(data.PayerPhone) : '-'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{texts.creationDate}</span>
                <span className={styles.infoValue}>
                  {data.CreationDate
                    ? `${formatDateShort(data.CreationDate)} ${formatTimeWithSeconds(data.CreationDate)}`
                    : '-'}
                </span>
              </div>
              {/* <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{texts.bankName}</span>
                <span className={styles.infoValue}>{data.BankName}</span>
              </div> */}
            </div>
          </div>
        </div>

        <div className={styles.actionsContainer}>
          <button className={styles.printButton} onClick={handlePrint}>
            <svg
              className="mr-2 h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            {texts.print}
          </button>
          <button className={styles.backButton} onClick={handleGoBack}>
            <svg
              className="mr-2 h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {texts.goBack}
          </button>
          <PrintReceiptModal
            isOpen={printOpen}
            onClose={() => setPrintOpen(false)}
            type="collection-order"
            data={data}
          />
        </div>
      </div>
    </PageContainer>
  )
}
